export const learnPt = {
  meta: {
    title: "Aprender a aprender — Lumen",
    description: "O guia interativo para entender como funcionam a memória, o cérebro e os melhores métodos de estudo.",
  },
  header: {
    badge: "Guia",
    title: "Aprender a aprender",
    subtitlePre: "Não precisas de estudar mais. Precisas de estudar ",
    subtitleBold: "melhor",
    subtitlePost: ". Eis como a tua mente realmente funciona.",
  },
  science: { heading: "A ciência de estudar" },
  methodsHeading: "Métodos de estudo",
  finalTip: {
    title: "O conselho final",
    body: "Não existe um método perfeito para todos. Experimenta, mede, adapta. O teu tutor de IA vai ajudar-te a escolher o método certo para cada matéria.",
  },
  lvl: { easy: "Fácil", media: "Médio" },
  badge: { accessible: "Acessível" },
  methodsList: { when: "Quando:" },
  dialog: {
    howTo: "Como fazer",
    example: "Exemplo prático",
    adaptationsTitle: "Variantes acessíveis",
    adaptationsFor: "Adaptações para",
    noAdaptations: "Nenhuma variante específica: o método base já é adequado.",
  },
  adaptMeta: {
    dsa: { label: "PEA", short: "dislexia, disortografia, discalculia" },
    adhd: { label: "PHDA", short: "atenção e hiperatividade" },
    bes: { label: "NEE", short: "necessidades educativas especiais" },
    focus: { label: "Foco", short: "dificuldade em manter a concentração" },
  },
  chapters: {
    memory: {
      title: "Como funciona a memória",
      body: "A memória não é uma gaveta: é um músculo. Sempre que RECORDAS uma informação — em vez de a reler — fixas-a de forma mais profunda. Isto chama-se **Recordação Ativa**.\n\nA curva do esquecimento (Ebbinghaus) mostra que, após 24h, recordamos 30% do que lemos passivamente. Revendo de forma distribuída (repetição espaçada), a retenção sobe para 80–90%.",
    },
    brain: {
      title: "Como funciona o cérebro ao estudar",
      body: "O cérebro tem dois modos: **focado** e **difuso** (relaxado). Alterná-los — com pausas e sono — é o que permite consolidar a aprendizagem. Estudar 8h seguidas sem pausas é contraproducente.",
    },
    mistakes: {
      title: "Erros mais comuns",
      body: "• **Reler** em vez de repetir em voz alta.\n• Estudar de forma **passiva** com sublinhados coloridos sem nunca recordar.\n• Deixar tudo para o último momento.\n• Não dormir o suficiente: sem sono REM, a memória não consolida.",
    },
    notes: {
      title: "Como tirar apontamentos",
      body: "O método **Cornell**: divide a página em 3 zonas (notas, palavras-chave, resumo). No final da aula, fecha o livro e preenche o resumo de memória. Aumenta a retenção até 34%.",
    },
    anxiety: {
      title: "Como gerir a ansiedade nos testes",
      body: "A respiração 4-7-8 (inspira 4s, retém 7s, expira 8s) baixa o cortisol. No dia anterior, não estudes temas novos: apenas **revê**. A confiança vem da preparação, não da ansiedade.",
    },
    forgetting: {
      title: "Como evitar esquecer",
      body: "Revê um tema após 1 dia, 3 dias, 7 dias, 21 dias. Cada recordação torna-o mais forte. É o segredo dos estudantes de medicina.",
    },
  },
  methods: {
    feynman: {
      d: "Explica como se fosse a uma criança de 10 anos.",
      when: "Quando precisas de compreender realmente um conceito.",
      how: [
        "Escolhe um conceito e escreve-o no topo de uma página.",
        "Explica-o em voz alta como se estivesses a falar com uma criança de 10 anos.",
        "Anota onde ficas preso ou usas palavras difíceis.",
        "Volta ao livro só nesses pontos e depois explica novamente do zero.",
      ],
      example: "Conceito: fotossíntese.\n\nExplicação para criança: \"As plantas comem luz. Tiram água pelas raízes e ar pelas folhas, e com a luz do sol cozinham açúcar para crescer. O ar que respiramos é o seu 'lixo'.\"\n\nBloqueio típico: não consegues explicar o que a clorofila tem a ver com isto. → Voltas ao livro, descobres que é a molécula verde que capta a luz, e explicas novamente: \"A clorofila é como um painel solar dentro da folha.\"",
      tip: "Se não consegues explicá-lo de forma simples, ainda não o compreendeste.",
      adaptations: {
        dsa: [
          "Grava a explicação em voz em vez de a escrever — podes ouvi-la depois.",
          "Usa o tutor de IA da app para reescrever o conceito em palavras mais simples.",
          "Usa uma fonte de alta legibilidade e ativa o modo PEA nas definições.",
        ],
        adhd: [
          "Um conceito de cada vez, com um temporizador visível de 10 minutos.",
          "Explica enquanto caminhas ou gesticulas: o movimento ajuda a manter o foco.",
          "Recompensa imediata no final (5 min de música, lanche, pausa).",
        ],
        bes: [
          "Usa desenhos ou ícones em vez de palavras difíceis.",
          "Pede a um amigo/familiar que te ouça: o feedback reduz a ansiedade.",
          "Divide a explicação em 3 mini-partes de 2 minutos cada.",
        ],
        focus: [
          "Remove distrações: telemóvel noutra divisão, notificações desligadas.",
          "Explica em voz alta como se estivesses a gravar um vídeo: ativa mais a atenção.",
        ],
      },
    },
    activeRecall: {
      d: "Fecha o livro. Repete em voz alta.",
      when: "Sempre. É o método nº 1.",
      how: [
        "Lê um parágrafo ou uma página.",
        "Fecha o livro.",
        "Repete em voz alta (ou escreve) tudo o que recordas.",
        "Reabre e corrige os erros.",
      ],
      example: "História — capítulo sobre a Revolução Francesa.\n\n1) Lê as causas (1789).\n2) Fecha o livro e tenta dizer: \"Crise económica, desigualdade entre os três estados, influência do Iluminismo, tomada da Bastilha a 14 de julho…\"\n3) Reabre-o: esqueceste-te da fome de 1788. Marca-o a vermelho.\n4) Tenta de novo passados 10 minutos — desta vez recordas.",
      tip: "Reler ≠ estudar. Se não consegues recordar, não sabes.",
      adaptations: {
        dsa: [
          "Repete em voz alta em vez de escrever: evita a leitura e escrita cansativas.",
          "Usa flashcards em áudio: grava a pergunta e ouve antes de responder.",
          "Pede ao tutor de IA para te fazer perguntas orais sobre o que estudaste.",
        ],
        adhd: [
          "Sessões curtas de 5-10 minutos, depois muda de tema para evitar o tédio.",
          "Transforma a revisão num jogo de pontos contigo mesmo.",
          "De pé ou a caminhar: o movimento ajuda a recordar.",
        ],
        bes: [
          "Começa com 3 perguntas simples antes de aumentar a dificuldade.",
          "Usa imagens-guia para ativar a memória (memória visual).",
        ],
        focus: [
          "Temporizador curto (10 min) e um objetivo claro: \"recordar 5 pontos\".",
          "Escreve a resposta de imediato sem reler: ativa a recordação de imediato.",
        ],
      },
    },
    pomodoro: {
      d: "25 min de foco + 5 min de pausa.",
      when: "Para começar a estudar sem procrastinar.",
      how: [
        "Escolhe UMA tarefa precisa (não 'estudar história', mas 'ler as páginas 45–52').",
        "Temporizador de 25 min. Telemóvel noutra divisão.",
        "5 min de pausa real (levanta-te, bebe água, olha para fora).",
        "Após 4 pomodoros: uma pausa longa de 15–30 min.",
      ],
      example: "15h00 — Pomodoro 1: ler as páginas 45–52 de história.\n15h25 — Pausa: caminhar na cozinha, beber água.\n15h30 — Pomodoro 2: fechar o livro e fazer Recordação Ativa do capítulo.\n15h55 — Pausa.\n16h00 — Pomodoro 3: resolver 5 exercícios de matemática.\n16h25 — Pausa longa, 20 min.",
      tip: "Usa o temporizador de estudo da app → modo Pomodoro.",
      adaptations: {
        dsa: [
          "Durante o pomodoro alterna 10 min de leitura + 15 min de atividade oral/visual.",
          "Na pausa não leias: descansa os olhos (olha ao longe, luz natural).",
        ],
        adhd: [
          "Reduz para **15 min de foco + 5 min de pausa** — mais realista para PHDA.",
          "Temporizador visível e sonoro. Escreve a tarefa num post-it à tua frente.",
          "Se te distraíres, não te julgues: repara no pensamento, volta à tarefa.",
        ],
        bes: [
          "Começa com apenas 1 pomodoro por dia e aumenta gradualmente.",
          "Usa um temporizador analógico ou ampulheta: o tempo torna-se concreto.",
        ],
        focus: [
          "Modo avião obrigatório durante os 25 minutos.",
          "Pausa REAL: sem redes sociais, sem vídeos. Só movimento ou água.",
        ],
      },
    },
    spacedRepetition: {
      d: "Rever em intervalos crescentes.",
      when: "Para recordar a longo prazo.",
      how: [
        "Estuda hoje (dia 0).",
        "Revê após 1 dia.",
        "Depois após 3 dias, 7 dias, 21 dias.",
        "Sempre que errares, recomeça a partir de 1 dia.",
      ],
      example: "Vocabulário de inglês, 20 palavras novas na segunda-feira.\n\n• Ter (dia 1): recorda as 20 palavras. Erras 4 → marca-as.\n• Qui (dia 3): recorda todas. Move as 4 erradas de volta para o dia 1.\n• Segunda seguinte (dia 7): revê apenas as estáveis.\n• Em 3 semanas: revisão final, 5 minutos.\n\nResultado: 90% de retenção após um mês, vs. 30% apenas com releitura.",
      tip: "Os flashcards da app já usam este método (5 caixas de Leitner).",
      adaptations: {
        dsa: [
          "Prefere flashcards com imagens ou áudio, não só texto.",
          "Na app podes criar flashcards por voz: grava a resposta em áudio.",
        ],
        adhd: [
          "Máximo 10 cartões por sessão: baralhos pequenos evitam a desistência.",
          "Revê sempre à mesma hora: a rotina ajuda mais do que a força de vontade.",
        ],
        bes: [
          "Usa cores diferentes por disciplina: ajuda a encontrar os cartões certos.",
          "Pede apoio para criar os primeiros cartões, depois torna-se independente.",
        ],
        focus: [
          "10 minutos de manhã logo ao acordar: o cérebro está mais recetivo.",
          "Fecha todas as outras abas/apps antes de começar o baralho.",
        ],
      },
    },
    cornellNotes: {
      d: "Notas em 3 zonas: notas / palavras-chave / resumo.",
      when: "Durante as aulas.",
      how: [
        "Divide a página em 3 zonas: coluna esquerda (palavras-chave), coluna direita (notas), faixa inferior (resumo).",
        "Durante a aula escreve só na coluna de notas (direita).",
        "Logo a seguir, preenche as palavras-chave à esquerda.",
        "Ao fim do dia, escreve 2-3 linhas de resumo no fundo.",
      ],
      example: "Página de biologia.\n\n[Notas à direita] A mitose tem 4 fases: prófase, metáfase, anáfase, telófase…\n[Palavras à esquerda] mitose · 4 fases · cromossomas\n[Resumo em baixo] A mitose divide uma célula em 2 idênticas. 4 fases. Usada para crescimento e reparação.",
      tip: "Ao tapares a coluna direita podes usar as palavras-chave como flashcards.",
      adaptations: {
        dsa: [
          "Escreve em letra de imprensa ou usa um tablet com fonte de alta legibilidade.",
          "Grava a aula (com autorização) e preenche as notas depois, com calma.",
          "Na coluna esquerda usa símbolos/ícones em vez de palavras quando possível.",
        ],
        adhd: [
          "Foca-te só na coluna direita durante a aula: uma coisa de cada vez.",
          "Preenche as palavras-chave e o resumo LOGO após a aula, não no dia seguinte.",
        ],
        bes: [
          "Modelo pré-impresso com as 3 zonas já divididas: reduz o esforço organizativo.",
          "Resumo em no máximo 2 frases: melhor curto e completo do que longo e a meio.",
        ],
        focus: [
          "Caneta sempre na mão: escrever mantém a atenção mesmo que não copies tudo.",
        ],
      },
    },
    blurting: {
      d: "Escreve tudo o que recordas, depois compara.",
      when: "Antes de um teste.",
      how: [
        "Escolhe um tema.",
        "Folha em branco. 5-10 min: escreve TUDO o que recordas, sem ordem específica.",
        "Abre o livro e compara.",
        "Assinala as lacunas a vermelho. São a tua próxima sessão de estudo.",
      ],
      example: "Tema: sistema circulatório.\n\nDespejo: \"Coração, 4 câmaras, aurícula direita e esquerda, ventrículos, artérias transportam sangue oxigenado exceto a pulmonar, veias…\"\n\nAbres o livro: esqueceste-te da válvula tricúspide e do papel dos capilares. É isso que revês esta noite.",
      tip: "O 'blurting' torna as lacunas visíveis em 10 minutos. Faz isto no dia anterior a um teste.",
      adaptations: {
        dsa: [
          "Faz o 'blurting' em voz alta: grava áudio em vez de escrever.",
          "Ao comparar com o livro, usa marcadores coloridos para as \"lacunas\".",
        ],
        adhd: [
          "Temporizador de 5 minutos: quando terminar, para, mesmo que não tenhas acabado.",
          "Um tema de cada vez. Não avances para o próximo sem comparar.",
        ],
        bes: [
          "Podes usar diagramas/setas em vez de frases completas.",
          "O primeiro despejo está sempre cheio de lacunas: isso é NORMAL, não um fracasso.",
        ],
        focus: [
          "Folha em branco à frente, tudo o resto longe. Escreve de seguida sem parar.",
        ],
      },
    },
    mindMapping: {
      d: "Representa conceitos visualmente.",
      when: "Para ligar ideias complexas.",
      how: [
        "Escreve o conceito central no meio de uma página.",
        "Ramifica com 3-5 ramos principais (subtemas).",
        "Cada ramo divide-se em sub-ramos com detalhes, exemplos, datas.",
        "Usa cores diferentes para macro-áreas e setas para conexões.",
      ],
      example: "Centro: \"Segunda Guerra Mundial\".\n\nRamos: Causas · Frentes · Pessoas · Consequências.\n\n• Causas → Tratado de Versalhes, crise de 1929, ascensão do nazismo.\n• Frentes → Europa, Pacífico, África.\n• Pessoas → Hitler, Churchill, Roosevelt, Estaline.\n• Consequências → ONU, Guerra Fria, descolonização.\n\nSetas entre 'crise de 1929' e 'ascensão do nazismo' para mostrar causa-efeito.",
      tip: "Na app podes gerar mapas mentais a partir dos teus apontamentos na secção Estudo.",
      adaptations: {
        dsa: [
          "Método IDEAL para PEA: aproveita a memória visual e reduz o texto.",
          "Usa cores, ícones e desenhos em vez de frases longas.",
          "Na app podes gerar o mapa a partir dos teus apontamentos com um clique.",
        ],
        adhd: [
          "Começa a partir do centro sem planear demasiado: acrescenta ramos à medida que pensas.",
          "Se te perderes, para tudo e volta ao conceito central.",
        ],
        bes: [
          "Mapa pequeno, máximo 3 ramos principais. Melhor simples e claro.",
          "Uma cor por ramo ajuda a memorizar as conexões.",
        ],
        focus: [
          "Constrói o mapa à mão numa folha grande: o gesto mantém-te atento.",
          "Faz um mapa de cada vez por tema — não misturas disciplinas.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "Precisas de iniciar sessão para guardar",
    saved: "Resultado guardado no teu perfil ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Descobre o teu método",
      discoverSubtitle: "7 perguntas rápidas para descobrires qual método de estudo funciona melhor para ti.",
      yourMethod: "O teu método",
      recommendedBadge: "Recomendado",
      savedOn: "guardado em {{date}}",
      thenPrefix: " · depois ",
      andWord: " e ",
      viewCard: "Ver ficha",
      retakeTest: "Refazer o teste",
    },
    dialogTitle: "Descobre o teu método",
    questionOf: "Pergunta {{n}} de {{total}}",
    resultsSubtitle: "Eis os métodos mais adequados para ti",
    noResults: "Sem resultados — tenta novamente.",
    recommendedBadge: "Recomendado",
    retake: "Refazer o teste",
    questions: [
      {
        q: "Quando estudas algo novo, o que mais te ajuda a compreender?",
        options: [
          "Explicá-lo em voz alta a alguém (ou a mim mesmo)",
          "Ver diagramas, conexões e desenhos",
          "Repetir em voz alta sem olhar para o livro",
        ],
      },
      {
        q: "Quão difícil é para ti começar a estudar?",
        options: [
          "Muito, ando sempre a procrastinar",
          "Bastante, mas ao fim de um tempo entro no ritmo",
          "Pouco, começo logo",
        ],
      },
      {
        q: "Como tiras apontamentos na aula?",
        options: [
          "Escrevo praticamente tudo",
          "Só as palavras-chave",
          "Ouço, e reescrevo em casa",
        ],
      },
      {
        q: "Ao fim de quanto tempo costumas esquecer o que estudaste?",
        options: [
          "Ao fim de alguns dias",
          "Ao fim de uma ou duas semanas",
          "Recordo durante muito tempo",
        ],
      },
      {
        q: "Como costumas preparar-te para um teste?",
        options: [
          "Revisão intensiva no dia anterior",
          "Um pouco de revisão de cada vez durante a semana",
          "Estudo tudo na véspera à noite",
        ],
      },
      {
        q: "Que atividade te ajuda mais a fixar a informação?",
        options: [
          "Escrever à mão",
          "Falar, explicar",
          "Desenhar mapas, diagramas, cores",
        ],
      },
      {
        q: "Que tipo de matérias pesam mais para ti?",
        options: [
          "Definições, vocabulário, fórmulas",
          "Conceitos abstratos para compreender",
          "Temas ligados entre si (história, literatura)",
        ],
      },
    ],
  },
};
